// PlayerTransferModal — Phase R5 Step 3.
//
// ════════════════════════════════════════════════════════════════════
// PURPOSE
// ════════════════════════════════════════════════════════════════════
//
// Shared modal that admin AND agent ends use to hand off a player
// to another agent identified by Unique Agent ID (UAID). Mounted
// from both:
//   * admin-frontend/src/pages/AdminUserDetail.tsx — admin override
//   * agent-frontend/src/pages/AdminUserDetail.tsx — agent self-
//     handoff (must currently own the player; backend enforces).
//
// ════════════════════════════════════════════════════════════════════
// THREE-STAGE FLOW (the "trust ceremony")
// ════════════════════════════════════════════════════════════════════
//
//   STAGE 1 — TYPE.
//     Single text input for the destination UAID. Auto-uppercased
//     on input (UAIDs are case-insensitive on the wire but the
//     UX standard is uppercase). Submit button DISABLED until
//     stage 2 succeeds.
//
//   STAGE 2 — VERIFY.
//     The user clicks "Verify" (or hits Enter). We call
//     GET /api/admin/agents/lookup?uaid=… and render the resolver
//     result — avatar + @username + the canonical UAID echoed back —
//     in a "Target confirmed" card.
//     A 404 surfaces a friendly "No active agent with that UAID"
//     message; the submit button stays disabled.
//
//   STAGE 3 — COMMIT.
//     With a valid resolution in state, the user clicks "Transfer"
//     and we PATCH /api/admin/users/:id/transfer { target_uaid }.
//     On success we close + fire onTransferred so the parent page
//     can refresh; on error we surface the server message.
//
// ════════════════════════════════════════════════════════════════════
// WHY VERIFY-BEFORE-TRANSFER (not auto-transfer-on-keystroke)
// ════════════════════════════════════════════════════════════════════
//
// UAIDs are short codes shared OUT-OF-BAND (chat, voice, paper).
// A typo silently routes the player to a stranger. The two-step
// "verify then commit" pattern forces the operator to read back
// the destination's display name + photo before the irreversible
// PATCH fires. This is the same trust-ceremony pattern banks use
// for inter-bank transfers ("payee verified: SULTAN SHARMA").
//
// We deliberately do NOT auto-verify on keystroke — that would
// race with the user typing and could leave the modal in an
// "almost verified" half-state. Verification is an explicit user
// action they can repeat.

import { useEffect, useRef, useState } from 'react';
import { adminApi } from '../lib/adminApi';
import { ApiError } from '../../lib/api';
import Icon from '../../components/Icon';
import AdminAvatar from './AdminAvatar';

// Resolver response shape — must match the
// adminAgentsController.lookupByUaid handler in
// shared/src/core/controllers/adminAgents.controller.js (R5 Step 2).
type ResolvedAgent = {
  id: number;
  uaid: string;
  username: string;
  avatar_url: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

type Props = {
  /** The player being transferred. We render their username in the
      modal header so the operator never confuses which row this
      modal applies to (a multi-tab user could have several open). */
  player: { id: number; username: string; first_name?: string | null; last_name?: string | null };
  /** Close without transferring. Either the X button or the
      backdrop click invokes this. */
  onClose: () => void;
  /** Fire on successful PATCH so the parent page can re-fetch the
      now-reassigned player + reflect the new owner inline. */
  onTransferred: () => void;
};

export default function PlayerTransferModal({ player, onClose, onTransferred }: Props) {
  // ── Stage 1 state — what the operator typed. ──
  const [uaidInput, setUaidInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Stage 2 state — resolver result. ──
  // null            → no verification attempted yet (or input changed since last verify)
  // {ok: false, …} → verification ran and failed
  // {ok: true, …}  → verification succeeded; submit button enables
  const [resolved, setResolved] = useState<ResolvedAgent | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);

  // ── Stage 3 state — final PATCH. ──
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Lock background scroll while modal open + autofocus the input
  // so the operator can start typing immediately.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── Input handler ──
  // Uppercase + strip spaces as the operator types so the visible
  // value matches the canonical UAID format. Pasting a code
  // surrounded by quotes / whitespace from a chat message Just Works.
  // Any keystroke INVALIDATES the previous resolution (otherwise an
  // operator could verify "ABC..." then change it to "XYZ..." and
  // the stale "Target confirmed" card would still authorise the
  // submit — a critical UX-driven security flaw).
  function onInputChange(raw: string) {
    setUaidInput(raw.toUpperCase().replace(/\s+/g, ''));
    setResolved(null);
    setVerifyErr(null);
    setSubmitErr(null);
  }

  // ── Stage 2: Verify ──
  async function verify() {
    if (!uaidInput || uaidInput.length < 4) {
      setVerifyErr('UAID is too short — at least 4 characters.');
      return;
    }
    setVerifying(true); setVerifyErr(null); setResolved(null);
    try {
      const r = await adminApi.get<{ agent: ResolvedAgent }>(
        `/admin/agents/lookup?uaid=${encodeURIComponent(uaidInput)}`,
      );
      setResolved(r.agent);
    } catch (e) {
      const err = e as ApiError;
      // 404 from the resolver = "no active agent with that UAID."
      // Display the server's message verbatim — it's already
      // operator-friendly and avoids leaking suspended-vs-missing.
      setVerifyErr(err.message || 'Could not verify UAID.');
    } finally {
      setVerifying(false);
    }
  }

  // Allow Enter inside the input to fire Verify (saves a tap on mobile).
  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (resolved) {
        // If already verified, Enter commits.
        submit();
      } else {
        verify();
      }
    }
  }

  // ── Stage 3: Commit ──
  async function submit() {
    if (!resolved) return;
    setSubmitting(true); setSubmitErr(null);
    try {
      await adminApi.patch(
        `/admin/users/${player.id}/transfer`,
        { target_uaid: resolved.uaid },
      );
      onTransferred();
      onClose();
    } catch (e) {
      const err = e as ApiError;
      setSubmitErr(err.message || 'Transfer failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // Backdrop — click closes the modal (no destructive write at this
    // point so backdrop-dismiss is safe; the GeoForcedModal pattern
    // of "no dismiss" is reserved for required-action flows).
    <div
      className="fixed inset-0 z-[70] grid place-items-start sm:place-items-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-transfer-title"
    >
      <div
        className="w-full max-w-md rounded-2xl
                   bg-white dark:bg-neutral-900
                   border border-neutral-200 dark:border-neutral-700
                   shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 min-w-0">
              <span className="grid place-items-center w-9 h-9 rounded-xl
                               bg-cyan-50 text-cyan-700
                               dark:bg-cyan-400/15 dark:text-cyan-300 shrink-0">
                <Icon name="swap_horiz" size={18} filled />
              </span>
              <div className="min-w-0">
                <h2
                  id="player-transfer-title"
                  className="text-base font-bold text-neutral-900 dark:text-white truncate">
                  Transfer player
                </h2>
                <div className="text-[11.5px] text-neutral-500 dark:text-white/55 truncate">
                  Hand off <span className="font-bold">{(((player.first_name || "") + " " + (player.last_name || "")).trim() || "—")}</span> to another agent.
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-2xl leading-none text-neutral-400 hover:text-neutral-700 dark:hover:text-white shrink-0"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Stage 1 — UAID input */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider text-neutral-500 dark:text-white/55 mb-1 font-bold">
              Target UAID
            </label>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={uaidInput}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder="ABCD1234"
                maxLength={12}
                disabled={submitting}
                className="flex-1 rounded-xl px-3 py-2 text-[14px] tabular-nums tracking-[.18em] uppercase font-mono
                           bg-white dark:bg-neutral-950/60
                           border border-neutral-300 dark:border-neutral-700
                           text-neutral-900 dark:text-white
                           focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/60
                           disabled:opacity-50 disabled:cursor-not-allowed"
                aria-invalid={!!verifyErr}
                aria-describedby={verifyErr ? 'uaid-error' : undefined}
              />
              <button
                type="button"
                onClick={verify}
                disabled={verifying || submitting || !uaidInput}
                className="px-4 py-2 rounded-xl text-[12px] font-bold uppercase tracking-wider
                           bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700 text-white
                           inline-flex items-center gap-1.5 transition
                           disabled:opacity-50 disabled:cursor-not-allowed">
                {verifying
                  ? <><Icon name="progress_activity" size={12} className="animate-spin" /> Verifying…</>
                  : <><Icon name="search" size={12} /> Verify</>}
              </button>
            </div>
            <p className="mt-1.5 text-[10.5px] text-neutral-500 dark:text-white/55">
              Ask the destination agent for their Unique Agent ID. UAIDs are case-insensitive — typing or pasting in either case is fine.
            </p>
            {verifyErr && (
              <p
                id="uaid-error"
                className="mt-2 text-[12px] text-rose-600 dark:text-rose-300 inline-flex items-center gap-1">
                <Icon name="error" size={12} filled /> {verifyErr}
              </p>
            )}
          </div>

          {/* Stage 2 — Resolved target card */}
          {resolved && (
            <div className="rounded-xl border-2 border-emerald-300 dark:border-emerald-500/40
                            bg-emerald-50 dark:bg-emerald-950/20 p-3">
              <div className="flex items-center gap-3">
                <AdminAvatar
                  url={resolved.avatar_url}
                  username={resolved.username}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[.2em] font-bold text-emerald-700 dark:text-emerald-300">
                    <Icon name="check_circle" size={11} filled className="inline -mt-0.5 mr-0.5" />
                    Target confirmed
                  </div>
                  <div className="text-[15px] font-extrabold text-neutral-900 dark:text-white truncate">
                    @{resolved.username}
                  </div>
                  <div className="text-[10.5px] font-mono tracking-wider text-emerald-700 dark:text-emerald-300/90">
                    {resolved.uaid}
                  </div>
                </div>
              </div>
              <p className="mt-2.5 text-[11.5px] text-neutral-600 dark:text-white/70 leading-relaxed">
                <strong>{(((player.first_name || "") + " " + (player.last_name || "")).trim() || "—")}</strong> will be reassigned to <strong>{(((resolved.first_name || "") + " " + (resolved.last_name || "")).trim() || "—")}</strong>. This is logged in the audit trail and cannot be silently undone.
              </p>
            </div>
          )}

          {/* Stage 3 — Commit error */}
          {submitErr && (
            <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10
                            border border-rose-200 dark:border-rose-400/30
                            text-rose-700 dark:text-rose-200 text-xs px-3 py-2">
              {submitErr}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-neutral-100 dark:border-neutral-800">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-full text-[12.5px] font-semibold
                       bg-white dark:bg-white/[0.04]
                       border border-neutral-200 dark:border-white/10
                       text-neutral-700 dark:text-white/70
                       hover:bg-neutral-50 dark:hover:bg-white/[0.08]
                       disabled:opacity-50 disabled:cursor-not-allowed">
            Cancel
          </button>
          <button
            onClick={submit}
            // Hard gate: submit only enables once we have a verified
            // resolved target AND we're not mid-PATCH. Without `resolved`
            // the operator could neither tap (button disabled) nor
            // press Enter (handler short-circuits) — there's no way
            // to commit without going through the verify ceremony.
            disabled={!resolved || submitting || verifying}
            className="px-4 py-2 rounded-full text-[12.5px] font-bold
                       bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700
                       text-white shadow-[0_2px_10px_rgba(6,182,212,0.4)]
                       inline-flex items-center gap-1.5 transition
                       disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting
              ? <><Icon name="progress_activity" size={12} className="animate-spin" /> Transferring…</>
              : <><Icon name="swap_horiz" size={12} /> Transfer</>}
          </button>
        </div>
      </div>
    </div>
  );
}
